import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setEmailSent(true);
        toast.success('Email envoyé !');
      } else {
        toast.error(data.detail || 'Une erreur est survenue');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 shadow-2xl text-center" data-testid="email-sent-card">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Email envoyé !</h1>
          <p className="text-gray-600 mb-6">
            Si un compte existe avec l'adresse <strong>{email}</strong>, 
            vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
          </p>
          
          <p className="text-sm text-gray-500 mb-6">
            Pensez à vérifier votre dossier spam si vous ne voyez pas l'email.
          </p>

          <div className="space-y-3">
            <Button
              onClick={() => setEmailSent(false)}
              variant="outline"
              className="w-full"
              data-testid="resend-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Renvoyer l'email
            </Button>
            
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full" data-testid="back-to-login-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la connexion
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8 shadow-2xl" data-testid="forgot-password-card">
        <div className="flex items-center justify-center mb-8">
          <Logo />
        </div>
        
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">Mot de passe oublié ?</h1>
        <p className="text-center text-gray-600 mb-8">
          Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email">Adresse email</Label>
            <Input
              id="email"
              type="email"
              placeholder="vous@entreprise.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1"
              data-testid="forgot-password-email-input"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]" 
            disabled={loading}
            data-testid="forgot-password-submit-btn"
          >
            {loading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link 
            to="/login" 
            className="text-[#2E59D7] hover:text-[#0061A0] font-semibold inline-flex items-center"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour à la connexion
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
